/* eslint-disable react/prop-types */
/**
 * POSTerminal — Sprint 2 top-level page (mock only, no backend).
 *
 * The floor view is now the LANDING SCREEN. It's what a cashier sees
 * on arrival at /super/pos/terminal. The billing screen is reached
 * only by:
 *   - tapping an OCCUPIED, BILLED or (freshly opened) table
 *   - pressing "New takeaway / delivery / room service"
 *
 * State lives in the reducer. Escape or the Floor button in the
 * billing top bar returns to the floor without closing the order.
 *
 * Sprint 3 · The ticket slip preview lives at this level so it
 *   surfaces from either the floor OR the billing screen the moment
 *   TICKET_SENT / TICKET_ITEM_CANCELLED is dispatched.
 */
import React, { useEffect, useReducer, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { posReducer, initialState, ORDER_TYPE, ORDER_STATE, ACT, getActive } from "../pos/posReducer";
import BillingScreen from "../pos/BillingScreen";
import FloorView from "../pos/FloorView";
import { TicketSlipDialog } from "../pos/Sprint3";
import { applyPosTheme, applyPosThemeVars, resolvePosTheme, ensurePosFontLoaded } from "../pos/posTheme";
import "../pos/pos-terminal.css";
import { buildDemoState } from "../pos/demoSeed";
import StaffLoginScreen from "../pos/StaffLoginScreen";
import { STAFF, DEMO_STAFF_ID } from "../pos/mockData";
import { findStaffById } from "../pos/posReducer";
import { getBinding, clearBinding } from "../pos/terminalBinding";
import OutletBindingScreen from "../pos/OutletBindingScreen";
import { listOpenOrders } from "../pos/posLive";
import { useLivePos } from "../pos/posLiveActions";
import { toast } from "sonner";
import { KeyRound, ShieldAlert, Settings as SettingsIcon, Store } from "lucide-react";
import {
  TillFloatPrompt,
} from "../pos/TillDialogs";
import { TillCloseDenominationDialog } from "../pos/TillShiftCloseFlow";
import { ManagerPinDialog as SharedPinDialog } from "../pos/Sprint4";

const API = process.env.REACT_APP_BACKEND_URL;
const ADMIN_LIKE = new Set(["admin", "super_admin", "area_manager"]);

// Sprint 6 correction 5 · The terminal component itself is oblivious
// to whether it is being reached via the authenticated cashier route
// or via the public /pos-demo entry point. `demo=true` swaps two
// things:
//   1. the initial reducer state is replaced by a pre-seeded demo
//      snapshot (tables occupied, tokens in queue, day+shift open)
//   2. an unobtrusive banner is rendered at the very top with a
//      "Reset demo" control that restores the same seed.
// No feature is stubbed, no flag gates any behaviour. The demo route
// exists specifically to be linkable without login when the backend
// lands. Do NOT wrap this component or the /pos-demo route in the
// upcoming tenant feature flag.
export default function POSTerminal({ demo = false, outletName, onExit }) {
  const auth = useAuth();
  const user = demo ? { name: "Demo cashier" } : auth?.user;
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(
    posReducer,
    null,
    () => {
      const s = demo ? buildDemoState() : initialState;
      // Sprint 7 correction 3f · demo pre-authenticates as the demo
      // cashier so a visitor is not gated by a login screen. Their
      // name is visible in the top bar and the Lock control still
      // works — locking shows the PIN screen with the demo hint.
      if (demo) {
        s.session.logged_in_staff_id = DEMO_STAFF_ID;
        s.session.staff_snapshot     = findStaffById(DEMO_STAFF_ID) || null;
        s.session.logged_in_at       = Date.now();
        s.session.locked             = false;
      }
      return s;
    },
  );
  const active = getActive(state);
  const [demoBanner, setDemoBanner] = React.useState(true);

  // ── Sprint 2 Checkpoint B.2 · Live persistence bridge ─────────────
  // stateRef must reflect the LATEST reducer state synchronously so
  // thunks that read `getState()` after a dispatch see fresh data,
  // even during rapid successive taps (React batches renders; a
  // useEffect-driven ref update runs after commit, which is too late).
  // Assigning during render is safe because useReducer's dispatcher
  // handles actions synchronously.
  const stateRef = useRef(state);
  stateRef.current = state;
  // Live mode: we have a POS token and we're not in demo. This is the
  // signal that mutations should hit /api/pos/* instead of staying
  // local to the reducer.
  const liveMode = !demo && !!state.session.logged_in_staff_id && !state.session.locked
    && typeof window !== "undefined" && !!window.localStorage.getItem("pos_token");
  const livePos = useLivePos({ dispatch, stateRef, live: liveMode });

  // Sprint 2 B.2 · Hydrate open orders from the server the first time
  // we enter live mode (POST-login OR post-reload). Without this, a
  // browser reload would lose the entire in-flight session.
  useEffect(() => {
    if (liveMode) { livePos.hydrate(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveMode]);

  // Sprint 7 correction 3d · idle-lock timer. Any interaction resets
  // the countdown; hitting the ceiling dispatches STAFF_LOCKED. Named
  // constant lives on state.session.idle_lock_seconds (default 120s)
  // so tenants can override at outlet level in production.
  const idleTimerRef = useRef(null);
  useEffect(() => {
    if (!state.session.logged_in_staff_id || state.session.locked) return undefined;
    // Sprint 3B correction · default 30 min (1800 s), min floor
    // 60 s. Configurable per outlet at PosSettings → Idle Lock.
    const secs = Math.max(60, Number(state.session.idle_lock_seconds) || 1800);
    const reset = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        dispatch({ type: ACT.STAFF_LOCKED });
      }, secs * 1000);
    };
    reset();
    const events = ["mousedown", "keydown", "touchstart", "wheel"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [state.session.logged_in_staff_id, state.session.locked, state.session.idle_lock_seconds]);

  // POS Sprint 6 correction 6 · Fetch the effective terminal theme
  // (default → tenant → outlet cascade) and repaint via
  // `applyPosTheme`. Runs whenever the resolved outlet changes and
  // once at mount. Demo mode stays on the RestroPulse default palette
  // so the sandbox never inherits an accidental tenant override.
  //
  // Sprint 8 correction · The outlet_id source chain was missing the
  // TERMINAL BINDING — a bound-but-not-yet-logged-in real POS never
  // knew its own outlet_id (state.session.outlet_id is only set after
  // PIN login), so the /pos/theme/public fetch was silently skipped
  // and the terminal rendered defaults until a full refresh. Adding
  // `binding?.outlet_id` fixes that.
  const themeOutletId = state.session.outlet_id
    || (typeof window !== "undefined" ? (getBinding()?.outlet_id) : null)
    || auth?.user?.outlet_id
    || auth?.activeOutlet?.id
    || null;
  // Sprint 3B correction · scoped theme root. All theme CSS vars
  // (both the `--pos-*` namespace and the shared `--color-primary`
  // Tailwind token) are written on THIS element. Descendants inherit
  // via the cascade; siblings — the platform admin shell, /pos/settings
  // rendered in `Layout.js` — never see the override.
  //
  // Sprint 8 correction · We ALSO write onto document.documentElement
  // so Radix Dialog/Popover portals (which mount under document.body,
  // OUTSIDE this component's tree) also inherit the theme. The whole
  // set is removed on unmount so the platform admin shell reverts to
  // its own brand tokens the instant the terminal is left.
  const themeRootRef = useRef(null);
  useEffect(() => {
    let alive = true;
    let appliedKeys = [];
    // Paint defaults on the SCOPED root so the first render is legible
    // even before the outlet override lands. If the wrapper isn't in
    // the DOM yet (very rare race) we quietly no-op — the next tick
    // will pick it up.
    if (themeRootRef.current) applyPosTheme(undefined, themeRootRef.current);
    const applyBoth = (vars) => {
      if (themeRootRef.current) applyPosThemeVars(vars, themeRootRef.current);
      // Portals — write to :root and remember every key so we can
      // strip them on unmount and never leak into the admin shell.
      if (typeof document !== "undefined") {
        const root = document.documentElement;
        Object.entries(vars || {}).forEach(([k, v]) => {
          if (v == null) return;
          root.style.setProperty(k, v);
          appliedKeys.push(k);
        });
        const family = vars["--pos-font-family"];
        if (family) root.style.setProperty("--pos-body-font", family);
      }
    };
    const block = (e) => e.preventDefault();
    window.addEventListener("contextmenu", block);
    if (!demo && themeOutletId) {
      axios.get(`${API}/api/pos/theme/public`, { params: { outlet_id: themeOutletId } })
        .then((r) => {
          if (!alive || !themeRootRef.current) return;
          const eff = r.data?.effective;
          if (!eff) return;
          // Sprint 7 · Route through the SHARED resolver so preview and
          // terminal cannot disagree. Also lazy-load the font family.
          ensurePosFontLoaded(eff.font_family || eff.fontFamily);
          if ((eff.heading_font || eff.headingFont) && (eff.heading_font || eff.headingFont) !== (eff.font_family || eff.fontFamily)) {
            ensurePosFontLoaded(eff.heading_font || eff.headingFont);
          }
          applyBoth(resolvePosTheme(eff));
        })
        .catch(() => { /* keep defaults on failure — never break the terminal */ });
    } else if (demo) {
      // Demo mode uses the built-in defaults but still needs to write
      // vars to :root so demo dialogs (elevation, cancel-reason) pick
      // up the correct primary/border/etc.
      applyBoth(resolvePosTheme({}));
    }
    return () => {
      alive = false;
      window.removeEventListener("contextmenu", block);
      // Strip every var we wrote onto :root so the admin shell reverts
      // to its own brand tokens. Scoped-root vars go away with the
      // wrapping element automatically.
      if (typeof document !== "undefined") {
        const root = document.documentElement;
        appliedKeys.forEach((k) => root.style.removeProperty(k));
        root.style.removeProperty("--pos-body-font");
      }
    };
  }, [demo, themeOutletId]);

  const exitTerminal = () => {
    if (onExit) { onExit(); return; }
    // POS Sprint 1 · The terminal is a tenant-facing route now. Demo
    // users land back on the marketing root; real tenant users bounce
    // back to their role-appropriate landing via the root redirector.
    navigate("/");
  };
  // POS Cleanup Pass 1 · leaving billing with an EMPTY OPEN order
  // must release the backend draft first — the reducer no longer
  // silently deletes it. Server refuses if any ticket/invoice/tender
  // exists, in which case we still navigate (the order remains).
  const goToFloor = async () => {
    const a = active?.order;
    const linesCount = active?.lines?.length || 0;
    if (livePos && a && a.state === ORDER_STATE.OPEN && linesCount === 0) {
      // fire-and-navigate: the server call may fail silently (network,
      // idempotent replay); the reducer flip to floor still happens.
      await livePos.releaseDraft(a.id, "EMPTY_BACK");
    }
    dispatch({ type: ACT.NAVIGATE_TO_FLOOR });
  };

  // Sprint 6 correction 5 · Reset demo — rebuild the seed snapshot in
  // place. Same fresh restaurant, same tables, same open day.
  const resetDemo = () => {
    dispatch({ type: ACT.DEMO_RESET, payload: { snapshot: buildDemoState() } });
  };

  const onSettleDone = () => {
    const type    = active?.order.type;
    const tableId = active?.order.table_id;
    const isTemp  = tableId && String(tableId).startsWith("t_temp_");
    if (type === ORDER_TYPE.DINE_IN && tableId) {
      if (isTemp) {
        dispatch({ type: ACT.TERMINAL_CLEARED, payload: { type: ORDER_TYPE.DINE_IN, temp_table_id: tableId } });
      } else {
        dispatch({ type: ACT.TABLE_MARKED_DIRTY, payload: { table_id: tableId } });
        dispatch({ type: ACT.TERMINAL_CLEARED, payload: { type: ORDER_TYPE.DINE_IN } });
      }
    } else {
      dispatch({ type: ACT.TERMINAL_CLEARED, payload: { type } });
    }
  };

  // Part 10 · dequeue the front slip; the dialog auto-shows on TICKET_SENT.
  const currentSlip = (state.session.slip_queue || [])[0] || null;

  const displayOutlet = outletName
    || (demo ? "Green Bamboo Bistro · Demo" : (auth?.activeOutlet?.name || auth?.user?.outlet_name || "RestroPulse POS"));
  const loggedStaff = state.session.staff_snapshot
    || (state.session.logged_in_staff_id ? findStaffById(state.session.logged_in_staff_id) : null);
  const displayUserName = loggedStaff ? loggedStaff.name : (user?.name || "—");
  const currentShift = (state.session.shifts || []).find((s) => !s.closed_at);
  const showLogin = !state.session.logged_in_staff_id || state.session.locked;

  // POS Cleanup Pass 3 · Auto-ensure the internal shift on PIN login.
  // Shifts are no longer a user-facing concept — the first cash-eligible
  // staff to sign in on an open business day silently opens one, and
  // every subsequent operator reuses the same shift for the rest of
  // the day. The guard ref prevents a race where an unresolved openShift
  // promise still races the next render.
  const autoShiftFor = useRef({ bizDate: null, inFlight: false });
  useEffect(() => {
    // POS Lifecycle Final Fix · Auto-ensure the internal shift for ANY
    // first PIN login of an open business day — including steward /
    // captain / bartender. The backend `open_shift` route already
    // accepts any authenticated role (see pos_orders.py "any role may
    // open" comment). The `silent` flag suppresses toasts, and 409
    // "already open" is transparently re-hydrated. Guarded by
    // `!state.session.business_date` so we never call the endpoint
    // before the server has an open Business Day (would 409 with a
    // confusing "Open the business day before opening a shift").
    if (!liveMode) return;
    if (!state.session.business_date) return;
    if (currentShift) return;
    if (!loggedStaff) return;
    if (autoShiftFor.current.inFlight) return;
    if (autoShiftFor.current.bizDate === state.session.business_date) return;
    autoShiftFor.current = { bizDate: state.session.business_date, inFlight: true };
    (async () => {
      try { await livePos.openShift({ silent: true }); }
      finally { autoShiftFor.current.inFlight = false; }
    })();
  }, [liveMode, state.session.business_date, currentShift, loggedStaff, livePos]);
  // Reset the auto-shift guard whenever a fresh business day starts
  // so the next day's first login triggers a new openShift attempt.
  useEffect(() => {
    if (autoShiftFor.current.bizDate && autoShiftFor.current.bizDate !== state.session.business_date) {
      autoShiftFor.current = { bizDate: null, inFlight: false };
    }
  }, [state.session.business_date]);

  // ── Sprint 2 B.2 · Till prompt gating ──────────────────────────────
  // After successful login (real or hydration) + open shift, offer a
  // till to any staff whose role carries the `settle` permission.
  // Managers see the two-step choice; cashiers/supervisors/outlet_admin
  // go straight to the float prompt. Steward/bartender never see it.
  const CAN_SETTLE = { cashier: true, supervisor: true, manager: true, outlet_admin: true };
  const shouldPromptTill =
       !!loggedStaff
    && !state.session.locked
    && !!currentShift
    && !state.session.current_till
    && !state.session.till_prompt_dismissed
    && !!CAN_SETTLE[loggedStaff.role]
    && !showLogin;
  const [tillBusy, setTillBusy] = useState(false);
  const [tillCloseOpen, setTillCloseOpen] = useState(false);
  // POS Cleanup Pass 2 · logout gated on till reconciliation. When the
  // cashier hits "Log out" with an open till, we open the same
  // TillCloseDenominationDialog and end the session only after the
  // close succeeds. Lock is separate and never touches the till.
  const [logoutPending, setLogoutPending] = useState(false);
  // Shared PIN request pipe for TillCloseDenominationDialog's
  // MANAGER_TAKEOVER branch. Self-close (the common cashier path)
  // never fires this, so the dialog stays closed.
  const [authRequest, setAuthRequest] = useState(null);
  const requestAuth = (reason, done) => setAuthRequest({ reason, done });

  const submitOpenTill = async (opening_float) => {
    setTillBusy(true);
    try { await livePos.openTill(opening_float); }
    finally { setTillBusy(false); }
  };
  // POS Cleanup Pass 2 · self-close via the consolidated denomination
  // dialog. The dialog computes counted/expected/variance and passes
  // the full payload here; we forward it verbatim to the server via
  // livePos.closeTill(counted_cash, extras). Success drops the till
  // and, when a logout is pending, ends the session.
  const submitCloseTill = async (payload) => {
    setTillBusy(true);
    try {
      const r = await livePos.closeTill(payload?.counted_cash || 0, payload || {});
      if (r) {
        setTillCloseOpen(false);
        if (logoutPending) {
          setLogoutPending(false);
          dispatch({ type: ACT.STAFF_LOGGED_OUT });
        }
      }
    } finally { setTillBusy(false); }
  };
  const requestOpenCloseTill = () => setTillCloseOpen(true);
  // POS Cleanup Pass 2 · logout wiring. If the caller has an open
  // till we open the reconciliation dialog first; otherwise we end
  // the session immediately. Manager PIN is NOT required for a
  // cashier's self-close — only variance justification is prompted.
  const requestLogout = () => {
    if (state.session.current_till) {
      setLogoutPending(true);
      setTillCloseOpen(true);
      return;
    }
    dispatch({ type: ACT.STAFF_LOGGED_OUT });
  };
  // Reopen the till float prompt when the operator dismissed it and
  // now wants to open a till. Simply clears the dismiss guard so the
  // reducer's `shouldPromptTill` reactivates.
  const requestOpenTill = () => {
    dispatch({ type: ACT.TILL_PROMPT_DISMISSED, payload: { dismissed: false } });
  };

  // POS Sprint 2 · Part 1 · Terminal-device binding.
  // A real (non-demo) launch resolves the outlet from the LOCAL device
  // binding — NOT from the platform's active outlet — so a manager who
  // switches outlets on the admin dashboard cannot accidentally point
  // the terminal at a different outlet. Refreshing binding requires
  // clicking the rebind pill on the PIN screen.
  const [binding, setBindingState] = useState(demo ? null : getBinding());
  const bindingRef = useRef(binding);
  useEffect(() => { bindingRef.current = binding; }, [binding]);

  const needsBinding = !demo && !binding;
  const boundOutletName = binding?.outlet_name || null;

  const finalDisplayOutlet = demo
    ? displayOutlet
    : (boundOutletName || displayOutlet);
  // Sprint 2 Consolidation Part 7 · stash the resolved outlet name on
  // session so reports (X/Z) can render it. Without this the reports
  // fell back to `outlet_code` and printed a 2-letter abbreviation.
  useEffect(() => {
    if (finalDisplayOutlet && state.session.outlet_name !== finalDisplayOutlet) {
      dispatch({ type: ACT.SESSION_HYDRATED, payload: { outlet_name: finalDisplayOutlet } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalDisplayOutlet]);
  const _dispOutlet = finalDisplayOutlet;

  // POS Sprint 1 correction 3 · Resolve the outlet_id we are about to
  // sign into. On a bound real terminal, the binding wins. On demo,
  // fall through to activeOutlet/user resolution (demo skips binding).
  const resolvedOutletId = demo
    ? (state.session.outlet_id || auth?.user?.outlet_id || auth?.activeOutlet?.id || null)
    : (binding?.outlet_id || null);

  // Setup-status probe — decides between the PIN screen and the
  // "POS setup required" first-time state. Runs only when we would
  // otherwise render the login screen and we're NOT in demo mode.
  const [setupStatus, setSetupStatus] = useState(null);   // null=loading, {has_active_staff}
  const [setupError, setSetupError]   = useState(null);
  useEffect(() => {
    if (demo || !showLogin || !resolvedOutletId) { setSetupStatus(null); return; }
    let alive = true;
    const token = window.localStorage.getItem('token');
    if (!token) { setSetupError('not-authenticated'); return; }
    axios.get(`${API}/api/pos/setup-status`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { outlet_id: resolvedOutletId },
    }).then((r) => { if (alive) setSetupStatus(r.data); })
      .catch((e) => {
        if (!alive) return;
        const s = e?.response?.status;
        if (s === 403) setSetupError('feature-off');
        else if (s === 401) setSetupError('not-authenticated');
        else setSetupError('unknown');
      });
    return () => { alive = false; };
  }, [demo, showLogin, resolvedOutletId]);

  const isPlatformAdmin = ADMIN_LIKE.has(auth?.user?.role || '');
  const needsSetup = !demo && showLogin && setupStatus && setupStatus.has_active_staff === false;
  const canShowPinScreen = demo
    || !showLogin
    || (setupStatus && setupStatus.has_active_staff === true);

  // POS Sprint 2 · Part 1 · Rebind flow.
  // Rebinding refuses when a shift is open OR any order at the bound
  // outlet is unsettled. This is the safety net that stops a manager
  // orphaning live orders by pointing the device at another outlet
  // mid-service. The check runs against the API (not local reducer
  // state) so a fresh device or a browser refresh cannot fool it.
  const [rebindError, setRebindError] = useState(null);
  const [rebindBusy, setRebindBusy]   = useState(false);
  const requestRebind = async () => {
    if (demo || !binding) return;
    setRebindBusy(true); setRebindError(null);
    try {
      // Per spec: rebinding requires an outlet_admin PIN AND no live
      // shift AND no unsettled orders. Enforcement here:
      //   1. Require an active POS session (pos_token present) — the
      //      operator MUST have signed in on the bound outlet first so
      //      the API open-orders check is real. Without that, silently
      //      clearing the binding would let anyone rebind by locking
      //      the terminal.
      //   2. Ask the API for OPEN orders at this outlet. Refuse if > 0.
      //   3. Refuse if the reducer's currentShift is not yet closed.
      const token = window.localStorage.getItem('pos_token');
      if (!token) {
        setRebindError(`To rebind, first sign in on ${binding.outlet_name} with an outlet-admin PIN. This proves the operator is authorised and lets the terminal check for open orders before rebinding.`);
        return;
      }
      let open = [];
      try { open = await listOpenOrders(); } catch (e) {
        setRebindError(`Could not verify open orders at ${binding.outlet_name}. Try again in a moment.`);
        return;
      }
      if (Array.isArray(open) && open.length > 0) {
        setRebindError(`Rebinding refused — ${open.length} order(s) still open at ${binding.outlet_name}. Settle them first.`);
        return;
      }
      if (currentShift && !currentShift.closed_at) {
        setRebindError(`Rebinding refused — a shift is open at ${binding.outlet_name}. Close the shift first.`);
        return;
      }
      // Wipe binding + POS session and force outlet selection.
      window.localStorage.removeItem('pos_token');
      clearBinding();
      setBindingState(null);
    } finally { setRebindBusy(false); }
  };

  return (
    <div ref={themeRootRef} data-testid="pos-theme-root" className="pos-terminal-root contents">
      {/* Sprint 2 · Part 1 · Device binding gate — a real terminal cannot
          proceed to a PIN screen or the setup flow until it has been
          bound to an outlet. `/pos-demo` bypasses this. */}
      {needsBinding && (
        <OutletBindingScreen
          onBound={(doc) => setBindingState(doc)}
          onExit={exitTerminal} />
      )}
      {!needsBinding && showLogin && !demo && needsSetup && (
        <PosSetupRequired
          outletId={resolvedOutletId}
          outletName={finalDisplayOutlet}
          isAdmin={isPlatformAdmin}
          onExit={exitTerminal}
          onGoToSettings={() => navigate('/pos/settings')} />
      )}
      {!needsBinding && showLogin && !demo && setupError && (
        <PosSetupBlocked kind={setupError} outletKnown={!!resolvedOutletId} onExit={exitTerminal} />
      )}
      {!needsBinding && showLogin && !demo && !resolvedOutletId && !setupError && (
        <PosSetupBlocked kind="no-outlet" outletKnown={false} onExit={exitTerminal} />
      )}
      {!needsBinding && showLogin && !demo && !needsSetup && !setupError && resolvedOutletId && !setupStatus && (
        <div className="fixed inset-0 z-[100] bg-[#0F1A16] flex items-center justify-center" data-testid="pos-setup-loading">
          <div className="animate-spin w-8 h-8 border-2 border-white/60 border-t-transparent rounded-full" />
        </div>
      )}
      {!needsBinding && canShowPinScreen && showLogin && (
        <StaffLoginScreen
          demo={demo}
          previouslyLoggedIn={state.session.locked}
          outletName={finalDisplayOutlet}
          outletId={resolvedOutletId}
          businessDate={state.session.business_date}
          onRebind={demo ? undefined : requestRebind}
          rebindBusy={rebindBusy}
          rebindError={rebindError}
          onUnlock={(staff) =>
            dispatch({ type: ACT.STAFF_LOGGED_IN, payload: { staff_id: staff.id, staff } })
          } />
      )}
      {/* Demo · minimised state · one-click restore (only when the
          user has explicitly hidden the flow banner). Small pill at
          the top-right of the viewport, well clear of the top strip
          buttons on the left/centre. */}
      {demo && !demoBanner && (
        <button type="button" onClick={() => setDemoBanner(true)}
          data-testid="pos-demo-banner-show"
          className="fixed top-1 right-2 z-[9999] h-5 px-2 rounded-full bg-amber-500 text-white text-[9px] font-semibold shadow hover:bg-amber-600">
          DEMO · show
        </button>
      )}
      {/* Sprint-1 · Session gate banner. Renders any time a
          billing action was refused (session_error) OR whenever the
          session gate is failing (no staff / no shift / steward
          waiting). Overlays the terminal but does not block navigation
          — closing a shift auto-clears it. */}
      {/* POS Cleanup Pass 3 · The former "Shift required" / "Waiting
          for shift" operational banner has been removed. Shifts now
          auto-open on the first PIN login of an open business day
          (see the auto-shift `useEffect` above), so this banner is
          unreachable in normal flow. The reducer's sessionGuard
          continues to refuse billing actions during the sub-second
          window between login and the server's openShift response,
          which is inaudible to the operator. */}
      {/* Terminal shell — flex-column so we can drop compact demo /
          feature banners as flow siblings ABOVE the top strip without
          fixed-position overlap. FloorView/BillingScreen are h-full,
          so this parent's h-screen governs both. */}
      <div className="h-screen w-full flex flex-col overflow-hidden">
        {/* Sprint 6 correction · Compact demo banner — flow child, NOT
            a fixed overlay, so it never covers the top strip controls. */}
        {demo && demoBanner && (
          <div
            data-testid="pos-demo-banner"
            className="h-7 shrink-0 flex items-center justify-center gap-2 px-3 bg-amber-100 border-b border-amber-300 text-[11px] text-amber-900">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
            <strong className="uppercase tracking-wider">Demo mode</strong>
            <span className="text-amber-800">·</span>
            <span className="truncate">Sample data. Resets on demand. Nothing is saved.</span>
            <button type="button" onClick={resetDemo}
              data-testid="pos-demo-reset"
              className="ml-2 h-5 px-2 rounded bg-amber-700 text-white font-semibold text-[10px] hover:bg-amber-800 shrink-0">
              Reset demo
            </button>
            <button type="button" onClick={() => setDemoBanner(false)}
              data-testid="pos-demo-banner-hide"
              className="ml-1 h-5 w-5 rounded hover:bg-amber-200 text-amber-900 shrink-0 flex items-center justify-center" title="Hide banner">×</button>
          </div>
        )}
        <div className="flex-1 min-h-0 relative">
          {/* Sprint 2 correction Part 6 · when nobody is logged in
              (or the terminal is locked) the PIN screen covers the
              chrome and NO order data is rendered underneath. Prevents
              a brief flash of the previous cashier's floor between
              logout and login and enforces the spec: "the terminal
              shows the PIN screen and nothing else." */}
          {showLogin
            ? null
            : (!active
              ? <FloorView state={state} dispatch={dispatch}
                  livePos={livePos}
                  outletName={finalDisplayOutlet}
                  userName={displayUserName}
                  onLock={() => dispatch({ type: ACT.STAFF_LOCKED })}
                  onCloseTill={requestOpenCloseTill}
                  onOpenTill={requestOpenTill}
                  onLogout={requestLogout}
                  onExit={exitTerminal} />
              : <BillingScreen state={state} dispatch={dispatch}
                  livePos={livePos}
                  onBack={goToFloor}
                  onSettle={onSettleDone}
                  onExit={exitTerminal}
                  outletName={finalDisplayOutlet}
                  onLock={() => dispatch({ type: ACT.STAFF_LOCKED })}
                  onCloseTill={requestOpenCloseTill}
                  userName={displayUserName} />)
          }
        </div>
      </div>

      {/* POS Cleanup Pass 2 · Single till-open prompt for every
          cash-eligible role. Managers use the same float prompt as
          cashiers — the old "handling cash?" gate was deleted. */}
      {shouldPromptTill && (
        <TillFloatPrompt
          staffName={loggedStaff?.name}
          staffRole={loggedStaff?.role}
          busy={tillBusy}
          onSubmit={submitOpenTill}
          onDismiss={() => dispatch({ type: ACT.TILL_PROMPT_DISMISSED })} />
      )}
      {/* POS Cleanup Pass 2 · Single till-close flow. The same
          denomination + variance dialog handles both the cashier
          self-close and the logout self-close path. Manager takeover
          runs from the TillShiftCloseFlow orchestrator in FloorView. */}
      {tillCloseOpen && state.session.current_till && (
        <TillCloseDenominationDialog
          open={true}
          till={state.session.current_till}
          mode="SELF"
          requestAuth={requestAuth}
          onCancel={() => {
            setTillCloseOpen(false);
            setLogoutPending(false);
          }}
          onSubmit={submitCloseTill} />
      )}
      {/* POS Cleanup Pass 2 · Shared PIN capture — only fires for the
          MANAGER_TAKEOVER branch inside TillCloseDenominationDialog.
          Self-close never routes through this. */}
      <SharedPinDialog
        open={!!authRequest}
        action_label={authRequest?.reason || ""}
        onCancel={() => setAuthRequest(null)}
        onConfirm={(auth) => {
          const req = authRequest;
          setAuthRequest(null);
          req?.done?.(auth);
        }} />

      {/* Part 10 · Slip preview · REMOVED in Sprint 2 Correction Part 1.
          The post-fire dialog implied the cashier could approve or
          reject a ticket that had already been sent. Confirmation now
          happens BEFORE the fire (SendKotConfirmDialog in BillingScreen).
          The reducer still enqueues slips into slip_queue for the
          thermal-print pipeline (Sprint 3); we auto-drain them here so
          the queue does not stall. Reprint is available on the fired
          ticket group row (data-testid pos-ticket-group-<no>-reprint). */}
      {currentSlip && (() => {
        // Auto-drain the slip on next tick so subsequent fires don't
        // pile up in the queue. Fire the effect from a hidden shim.
        setTimeout(() => dispatch({ type: ACT.SLIP_ACKNOWLEDGED }), 0);
        return null;
      })()}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// POS Sprint 1 correction · First-time setup + blocked states.
// Shown INSTEAD of the PIN screen when the outlet has no active POS
// staff yet, or when the caller cannot reach the setup-status endpoint
// (feature off, not authenticated, no outlet resolvable).
// ─────────────────────────────────────────────────────────────────────
function PosSetupRequired({ outletId: _outletId, outletName, isAdmin, onExit, onGoToSettings }) {
  return (
    <div className="fixed inset-0 z-[100] bg-[#0F1A16] flex items-center justify-center px-6"
         data-testid="pos-setup-required">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-7 flex flex-col gap-4">
        <div className="flex items-center gap-2 text-[color:var(--color-primary)]">
          <KeyRound className="w-5 h-5" />
          <div className="text-[10px] uppercase tracking-widest text-[#5C6B64] font-semibold">
            POS setup required
          </div>
        </div>
        <div>
          <h2 className="text-[20px] font-semibold text-[#1A2421]" data-testid="pos-setup-required-title">
            No staff PINs configured yet
          </h2>
          <p className="text-[13px] text-[#5C6B64] mt-2 leading-relaxed">
            {outletName ? <>An administrator must create at least one POS user for <strong>{outletName}</strong> before the terminal can be opened.</> : <>An administrator must create at least one POS user before the terminal can be opened.</>}
          </p>
        </div>
        {isAdmin ? (
          <>
            <button type="button" onClick={onGoToSettings}
                    data-testid="pos-setup-configure"
                    className="h-12 rounded-lg bg-[color:var(--color-primary)] text-white font-semibold text-[14px] hover:brightness-110 flex items-center justify-center gap-2">
              <SettingsIcon className="w-4 h-4" /> Configure POS access
            </button>
            <div className="text-[11px] text-[#5C6B64] text-center">
              Opens POS Settings — pick this outlet, add a staff member and share the generated PIN.
            </div>
          </>
        ) : (
          <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-[12px] px-3 py-2.5"
               data-testid="pos-setup-required-non-admin">
            Please contact your administrator to configure POS access for this outlet.
          </div>
        )}
        <button type="button" onClick={onExit}
                data-testid="pos-setup-exit"
                className="h-10 rounded-lg border border-[#E2E8DF] text-[#1A2421] font-medium text-[13px] hover:bg-[#F4F7F5]">
          Exit terminal
        </button>
      </div>
    </div>
  );
}

function PosSetupBlocked({ kind, outletKnown: _outletKnown, onExit }) {
  const copy = kind === 'no-outlet'
    ? {
        title: 'No outlet selected',
        body:  'Pick an outlet at the top of the platform before opening the POS terminal. Managers should sign in against the outlet they operate.',
      }
    : kind === 'feature-off'
      ? {
          title: 'POS is not enabled for this outlet',
          body:  'Ask your administrator to enable POS on this tenant and try again.',
        }
      : kind === 'not-authenticated'
        ? {
            title: 'Sign in required',
            body:  'Your platform session has expired. Sign in again to open the POS terminal.',
          }
        : {
            title: 'Could not reach POS setup service',
            body:  'Please try again in a moment. If the problem persists, contact support.',
          };
  return (
    <div className="fixed inset-0 z-[100] bg-[#0F1A16] flex items-center justify-center px-6"
         data-testid="pos-setup-blocked">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-7 flex flex-col gap-4">
        <div className="flex items-center gap-2 text-amber-700">
          <ShieldAlert className="w-5 h-5" />
          <div className="text-[10px] uppercase tracking-widest font-semibold">POS unavailable</div>
        </div>
        <div>
          <h2 className="text-[20px] font-semibold text-[#1A2421]">{copy.title}</h2>
          <p className="text-[13px] text-[#5C6B64] mt-2 leading-relaxed">{copy.body}</p>
        </div>
        <button type="button" onClick={onExit}
                data-testid="pos-setup-blocked-exit"
                className="h-11 rounded-lg bg-[color:var(--color-primary)] text-white font-semibold text-[14px] hover:brightness-110">
          Exit terminal
        </button>
      </div>
    </div>
  );
}
